import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Plus, Trash, PencilSimple, Files, ArrowsClockwise, MagnifyingGlass,
  CheckCircle, CircleNotch, Lightning
} from '@phosphor-icons/react';
import AdminLayout from '../components/AdminLayout';
import StatusTimeline from '../components/StatusTimeline';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import API_URL from '../config';
import { ReportTierAuditPanel } from '../components/ReportTierAuditPanel';

const STATUS_OPTIONS = [
  { value: 'filed', label: 'Filed' },
  { value: 'office_action', label: 'Office Action Received' },
  { value: 'office_action_cleared', label: 'Office Action Cleared' },
  { value: 'published', label: 'Published for Opposition' },
  { value: 'opposition_cleared', label: 'Opposition Cleared' },
  { value: 'registered', label: 'Registered' },
  { value: 'dead', label: 'Dead (Abandoned/Canceled)' }
];

function determinePhase(tm) {
  if (!tm.live) return 'dead';
  if (tm.registered) return 'registered';
  if (tm.published) return 'published';
  return 'filed';
}

function isLogoMark(markDrawingType) {
  if (!markDrawingType) return false;
  const type = markDrawingType.toUpperCase();
  return type !== 'STANDARD_CHARACTERS' && type !== 'TYPED_DRAWING' && type !== '';
}

export default function AdminMatters() {
  const [matters, setMatters] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(null);

  // Import flow state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importClientId, setImportClientId] = useState('');
  const [serialInput, setSerialInput] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoveredTMs, setDiscoveredTMs] = useState([]);
  const [discoveredOwners, setDiscoveredOwners] = useState([]);
  const [selectedTMs, setSelectedTMs] = useState(new Set());
  const [importing, setImporting] = useState(false);

  // Edit flow state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editMatter, setEditMatter] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [mattersRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/matters`),
        axios.get(`${API_URL}/api/admin/clients`)
      ]);
      setMatters(mattersRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscover = async () => {
    if (!serialInput.trim()) {
      toast.error('Enter at least one serial number');
      return;
    }
    const serials = serialInput.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => s && /^\d+$/.test(s));
    if (serials.length === 0) {
      toast.error('No valid serial numbers found');
      return;
    }

    setDiscovering(true);
    setDiscoveredTMs([]);
    setDiscoveredOwners([]);
    try {
      const response = await axios.post(`${API_URL}/api/admin/tmtko/discover`, { serial_numbers: serials });
      const { trademarks, owners_found } = response.data;
      setDiscoveredTMs(trademarks);
      setDiscoveredOwners(owners_found);
      // Auto-select all
      setSelectedTMs(new Set(trademarks.map(t => t.serial_number)));
      toast.success(`Found ${trademarks.length} trademarks across ${owners_found.length} owner(s)`);
    } catch (error) {
      toast.error('TM TKO search failed');
    } finally {
      setDiscovering(false);
    }
  };

  const handleToggleSelect = (serial) => {
    setSelectedTMs(prev => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial);
      else next.add(serial);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedTMs.size === discoveredTMs.length) {
      setSelectedTMs(new Set());
    } else {
      setSelectedTMs(new Set(discoveredTMs.map(t => t.serial_number)));
    }
  };

  const handleImportSelected = async () => {
    if (!importClientId) {
      toast.error('Select a client first');
      return;
    }
    if (selectedTMs.size === 0) {
      toast.error('Select at least one trademark');
      return;
    }

    setImporting(true);
    let imported = 0;
    let skipped = 0;

    for (const tm of discoveredTMs) {
      if (!selectedTMs.has(tm.serial_number)) continue;
      
      // Check if matter already exists for this client + serial
      const exists = matters.some(m => m.client_id === importClientId && m.serial_number === tm.serial_number);
      if (exists) { skipped++; continue; }

      try {
        await axios.post(`${API_URL}/api/admin/matters`, {
          client_id: importClientId,
          trademark_name: tm.mark,
          description: tm.owners?.join(', ') || '',
          status_phase: determinePhase(tm),
          serial_number: tm.serial_number,
          registration_number: tm.registration_number || null,
          filing_date: tm.filing_date || null,
          registration_date: tm.registration_date || null,
          mark_drawing_type: tm.mark_drawing_type || null,
          notes: `Auto-imported from TM TKO. Status code: ${tm.status_code}. Live: ${tm.live}.`
        });
        imported++;
      } catch (error) {
        console.error(`Failed to import ${tm.mark}:`, error);
      }
    }

    toast.success(`Imported ${imported} trademark(s)${skipped > 0 ? `, ${skipped} already existed` : ''}`);
    setIsImportOpen(false);
    resetImport();
    fetchData();
    setImporting(false);
  };

  const resetImport = () => {
    setImportClientId('');
    setSerialInput('');
    setDiscoveredTMs([]);
    setDiscoveredOwners([]);
    setSelectedTMs(new Set());
  };

  const handleEditMatter = (matter) => {
    setEditMatter(matter);
    setEditData({
      trademark_name: matter.trademark_name,
      status_phase: matter.status_phase,
      serial_number: matter.serial_number || '',
      registration_number: matter.registration_number || '',
      filing_date: matter.filing_date || '',
      registration_date: matter.registration_date || '',
      renewal_date: matter.renewal_date || '',
      notes: matter.notes || ''
    });
    setIsEditOpen(true);
  };

  const handleUpdateMatter = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/admin/matters/${editMatter.id}`, editData);
      toast.success('Matter updated');
      setIsEditOpen(false);
      setEditMatter(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update matter');
    }
  };

  const handleDeleteMatter = async (matterId) => {
    if (!window.confirm('Delete this matter?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/matters/${matterId}`);
      toast.success('Matter deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete matter');
    }
  };

  const handleSyncTMTKO = async (matterId) => {
    setSyncing(matterId);
    try {
      const response = await axios.post(`${API_URL}/api/admin/matters/${matterId}/sync-tmtko`);
      if (response.data.tm_data) toast.success('Synced with TM TKO');
      else toast.info(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to sync');
    } finally {
      setSyncing(null);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown';
  };

  const [reportInputs, setReportInputs] = useState({});
  const [reportTierOverride, setReportTierOverride] = useState({});
  const [publishing, setPublishing] = useState(null);

  const handlePublishReport = async (matterId) => {
    const url = (reportInputs[matterId] || '').trim();
    if (!url) { toast.error('Paste the client-ready report link first'); return; }
    setPublishing(matterId);
    try {
      const body = { url };
      if (reportTierOverride[matterId]) body.tier_override = reportTierOverride[matterId];
      const res = await axios.post(`${API_URL}/api/admin/matters/${matterId}/publish-report`, body);
      toast.success(`Published — ${res.data.tier}${res.data.hubspot_written ? ' · saved to HubSpot' : ''}`);
      setReportInputs({ ...reportInputs, [matterId]: '' });
      setReportTierOverride({ ...reportTierOverride, [matterId]: '' });
      fetchData();
    } catch (error) {
      const d = error.response?.data?.detail;
      if (d && typeof d === 'object' && d.needs_manual_tier) {
        toast.error(`${d.message} Pick a tier below and try again.`);
        setReportTierOverride({ ...reportTierOverride, [matterId]: reportTierOverride[matterId] || 'Storm Watch' });
      } else {
        toast.error(typeof d === 'string' ? d : (d?.message || 'Could not publish report'));
      }
    } finally {
      setPublishing(null);
    }
  };

  const handleUnpublishReport = async (matterId) => {
    try {
      await axios.post(`${API_URL}/api/admin/matters/${matterId}/unpublish-report`, {});
      toast.success('Report unpublished');
      fetchData();
    } catch (error) {
      toast.error('Could not unpublish');
    }
  };

  const filteredMatters = matters.filter(m =>
    m.trademark_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getClientName(m.client_id).toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#ffd100] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-matters">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Manage</p>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d' }}>Matters</h1>
          </div>
          <Dialog open={isImportOpen} onOpenChange={(open) => { setIsImportOpen(open); if (!open) resetImport(); }}>
            <DialogTrigger asChild>
              <button className="btn-primary flex items-center gap-2" disabled={clients.length === 0} data-testid="create-matter-btn">
                <Lightning weight="bold" className="w-4 h-4" />
                Import from TM TKO
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold" style={{ color: '#141b4d' }}>
                  Import Trademarks from TM TKO
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-5 mt-4">
                {/* Step 1: Select client */}
                <div>
                  <label className="label-uppercase block mb-2">Client</label>
                  <Select value={importClientId} onValueChange={setImportClientId}>
                    <SelectTrigger className="input-swiss" data-testid="import-client-select">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 2: Enter serial numbers */}
                <div>
                  <label className="label-uppercase block mb-2">Serial Numbers</label>
                  <textarea
                    value={serialInput}
                    onChange={(e) => setSerialInput(e.target.value)}
                    className="input-swiss min-h-[80px]"
                    placeholder="Enter one or more serial numbers (comma or line separated)&#10;e.g., 97573000, 99048632, 99654323"
                    data-testid="serial-numbers-input"
                  />
                  <p className="text-xs mt-1" style={{ color: '#9a9488' }}>
                    We'll find these trademarks AND discover any others with the same owner(s)
                  </p>
                </div>

                {/* Search button */}
                <button
                  onClick={handleDiscover}
                  disabled={discovering || !serialInput.trim()}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="discover-btn"
                >
                  {discovering ? (
                    <><CircleNotch weight="bold" className="w-5 h-5 animate-spin" />Searching TM TKO...</>
                  ) : (
                    <><MagnifyingGlass weight="bold" className="w-5 h-5" />Search & Discover</>
                  )}
                </button>

                {/* Results */}
                {discoveredTMs.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-semibold" style={{ color: '#141b4d' }}>
                          Found {discoveredTMs.length} trademark(s)
                        </p>
                        <p className="text-xs" style={{ color: '#9a9488' }}>
                          Owners: {discoveredOwners.join(', ')}
                        </p>
                      </div>
                      <button onClick={handleSelectAll} className="text-sm font-medium hover:underline" style={{ color: '#1d57a5' }}
                        data-testid="select-all-btn">
                        {selectedTMs.size === discoveredTMs.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {discoveredTMs.map((tm) => {
                        const isSelected = selectedTMs.has(tm.serial_number);
                        const alreadyExists = matters.some(m => m.client_id === importClientId && m.serial_number === tm.serial_number);
                        return (
                          <div
                            key={tm.serial_number}
                            className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                              alreadyExists ? 'opacity-50' : isSelected ? 'border-[#141b4d] bg-[#141b4d]/5' : 'border-[#d4cfc5] hover:border-[#141b4d]'
                            }`}
                            onClick={() => !alreadyExists && handleToggleSelect(tm.serial_number)}
                            data-testid={`tm-result-${tm.serial_number}`}
                          >
                            <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'border-[#141b4d] bg-[#141b4d]' : 'border-[#d4cfc5]'
                            }`}>
                              {isSelected && <CheckCircle weight="fill" className="w-4 h-4 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm" style={{ color: '#101820' }}>{tm.mark}</p>
                                {isLogoMark(tm.mark_drawing_type) && (
                                  <span className="text-xs font-medium px-1.5 py-0.5" style={{ backgroundColor: '#141b4d', color: '#ffd100' }}>LOGO</span>
                                )}
                                <span className={`status-badge ${tm.registered ? 'status-registered' : tm.published ? 'status-published' : !tm.live ? 'status-dead' : 'status-filed'}`}>
                                  {tm.registered ? 'Registered' : tm.published ? 'Published' : !tm.live ? 'Dead' : 'Filed'}
                                </span>
                                {alreadyExists && (
                                  <span className="text-xs px-2 py-0.5 bg-[#f2e9db]" style={{ color: '#9a9488' }}>Already added</span>
                                )}
                              </div>
                              <p className="text-xs" style={{ color: '#9a9488' }}>
                                Serial: {tm.serial_number}
                                {tm.registration_number && ` • Reg: ${tm.registration_number}`}
                                {tm.filing_date && ` • Filed: ${tm.filing_date}`}
                              </p>
                              <p className="text-xs" style={{ color: '#9a9488' }}>
                                Owner: {tm.owners?.join(', ')}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Import button */}
                    <button
                      onClick={handleImportSelected}
                      disabled={importing || selectedTMs.size === 0 || !importClientId}
                      className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
                      data-testid="import-selected-btn"
                    >
                      {importing ? (
                        <><CircleNotch weight="bold" className="w-5 h-5 animate-spin" />Importing...</>
                      ) : (
                        <>Import {selectedTMs.size} Trademark(s) as Matters</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <ReportTierAuditPanel />

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#9a9488' }} />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="input-swiss !pl-10" placeholder="Search matters..." data-testid="search-matters-input" />
        </div>

        {/* Matters List */}
        {filteredMatters.length === 0 ? (
          <div className="card-swiss text-center py-12">
            <Files weight="duotone" className="w-12 h-12 mx-auto mb-4" style={{ color: '#9a9488' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: '#141b4d' }}>
              {searchQuery ? 'No matters found' : 'No Matters Yet'}
            </h3>
            <p style={{ color: '#9a9488' }}>
              {searchQuery ? 'Try a different search.' : 'Import trademarks from TM TKO to start tracking.'}
            </p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {filteredMatters.map((matter, index) => (
              <motion.div key={matter.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }} className="card-swiss" data-testid={`matter-row-${matter.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold" style={{ color: '#141b4d' }}>{matter.trademark_name}</h3>
                      {isLogoMark(matter.mark_drawing_type) && (
                        <span className="text-xs font-medium px-2 py-0.5" style={{ backgroundColor: '#141b4d', color: '#ffd100' }}>LOGO</span>
                      )}
                      <span className={`status-badge status-${matter.status_phase}`}>{matter.status_phase.replace('_', ' ')}</span>
                    </div>
                    <p className="text-sm" style={{ color: '#9a9488' }}>
                      Client: <span className="font-medium" style={{ color: '#101820' }}>{getClientName(matter.client_id)}</span>
                    </p>
                    <div className="flex flex-wrap gap-4 mt-1 text-sm" style={{ color: '#9a9488' }}>
                      {matter.serial_number && <span>Serial: {matter.serial_number}</span>}
                      {matter.registration_number && <span>Reg: {matter.registration_number}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleSyncTMTKO(matter.id)} disabled={syncing === matter.id}
                      className="p-2 hover:bg-[#f2e9db] transition-colors" title="Sync with TM TKO"
                      data-testid={`sync-matter-${matter.id}`}>
                      <ArrowsClockwise weight="regular" className={`w-5 h-5 ${syncing === matter.id ? 'animate-spin' : ''}`} style={{ color: '#9a9488' }} />
                    </button>
                    <button onClick={() => handleEditMatter(matter)}
                      className="p-2 hover:bg-[#f2e9db] transition-colors" data-testid={`edit-matter-${matter.id}`}>
                      <PencilSimple weight="regular" className="w-5 h-5" style={{ color: '#9a9488' }} />
                    </button>
                    <button onClick={() => handleDeleteMatter(matter.id)}
                      className="p-2 hover:bg-red-50 transition-colors text-[#A1A1AA] hover:text-red-500" data-testid={`delete-matter-${matter.id}`}>
                      <Trash weight="regular" className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <StatusTimeline currentPhase={matter.status_phase} />

                {/* Search Report — paste-to-publish (Phase 3, team-facing) */}
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid={`report-panel-${matter.id}`}>
                  {matter.search_report_url ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs font-semibold px-2 py-1" style={{ backgroundColor: '#006747', color: '#f2e9db' }}>
                        REPORT PUBLISHED · {matter.search_report_tier || 'tier?'}
                      </span>
                      <span className="text-xs" style={{ color: '#9a9488' }}>
                        {matter.search_report_package ? `${matter.search_report_package} · ` : ''}{matter.search_report_filename}
                      </span>
                      <a href={matter.search_report_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs font-medium hover:underline" style={{ color: '#1d57a5' }}>View file</a>
                      <button onClick={() => handleUnpublishReport(matter.id)}
                        className="text-xs font-medium hover:underline" style={{ color: '#b91c1c' }}
                        data-testid={`unpublish-report-${matter.id}`}>Unpublish</button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <span className="label-uppercase text-xs" style={{ color: '#9a9488' }}>Search Report</span>
                      <input type="text" placeholder="Paste client-ready Drive report link…"
                        value={reportInputs[matter.id] || ''}
                        onChange={(e) => setReportInputs({ ...reportInputs, [matter.id]: e.target.value })}
                        className="input-swiss flex-1 text-sm py-1.5" data-testid={`report-input-${matter.id}`} />
                      {reportTierOverride[matter.id] !== undefined && reportTierOverride[matter.id] !== '' && (
                        <select value={reportTierOverride[matter.id]}
                          onChange={(e) => setReportTierOverride({ ...reportTierOverride, [matter.id]: e.target.value })}
                          className="input-swiss text-sm py-1.5" data-testid={`report-tier-${matter.id}`}>
                          {['Clear Skies', 'Partly Cloudy', 'Storm Watch', 'Tornado Warning'].map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      )}
                      <button onClick={() => handlePublishReport(matter.id)} disabled={publishing === matter.id}
                        className="btn-primary text-sm py-1.5 px-4 whitespace-nowrap" data-testid={`publish-report-${matter.id}`}>
                        {publishing === matter.id ? 'Publishing…' : 'Publish'}
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={(open) => { setIsEditOpen(open); if (!open) setEditMatter(null); }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold" style={{ color: '#141b4d' }}>
                Edit: {editMatter?.trademark_name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateMatter} className="space-y-4 mt-4">
              <div>
                <label className="label-uppercase block mb-2">Status Phase</label>
                <Select value={editData.status_phase} onValueChange={(v) => setEditData({ ...editData, status_phase: v })}>
                  <SelectTrigger className="input-swiss" data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-uppercase block mb-2">Renewal Date</label>
                  <input type="date" value={editData.renewal_date} onChange={(e) => setEditData({ ...editData, renewal_date: e.target.value })}
                    className="input-swiss" data-testid="edit-renewal-date" />
                </div>
                <div>
                  <label className="label-uppercase block mb-2">Registration #</label>
                  <input type="text" value={editData.registration_number} onChange={(e) => setEditData({ ...editData, registration_number: e.target.value })}
                    className="input-swiss" data-testid="edit-reg-number" />
                </div>
              </div>
              <div>
                <label className="label-uppercase block mb-2">Notes</label>
                <textarea value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  className="input-swiss min-h-[80px]" data-testid="edit-notes" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setIsEditOpen(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" className="btn-primary flex-1" data-testid="edit-submit-btn">Save Changes</button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
